<script setup>
import { format, parseISO } from "date-fns";

const toast = useToast();

const { webinar } = defineProps({
  webinar: Object,
});

const { status } = useAuth();
const isLoading = ref(false);

const formattedDate = computed(() => {
  const parsedDate = parseISO(webinar.start_time);
  return format(parsedDate, "dd MMMM yyyy");
});

const formattedTime = computed(() => {
  const parsedDate = parseISO(webinar.start_time);
  return format(parsedDate, "hh:mm a");
});

const joinWebinar = async () => {
  if (webinar.meeting_link) {
    navigateTo(webinar.meeting_link, {
      external: true,
      open: {
        target: "_blank",
      },
    });
  } else {
    toast.add({
      severity: "danger",
      summary: "Webinar does not have meeting link.",
      life: 3000,
    });
  }
};

const { data: studentWebinars } = useApiFetch("webinarBystudent", {
  method: "GET",
  key: "upcoming-webinars-by-student",
  queryKey: "upcoming-webinars-by-student",
  query: {
    status: "upcoming",
  },
  transform(webinars) {
    return webinars?.data;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      return;
    }
  },
  onResponseError({ response }) {
    if (response.status === 401) {
      return;
    }
  },
});

const isJoined = computed(() => {
  return (
    studentWebinars?.value?.map((wb) => wb.id)?.includes(webinar.id) || false
  );
});

const registerWebinar = async () => {
  if (status.value !== "authenticated") return;
  isLoading.value = true;
  await useApiFetch(`webinars/${webinar.id}/register`, {
    method: "POST",
    onResponse() {
      refreshNuxtData("upcoming-webinars-by-student");
      toast.add({
        severity: "success",
        summary: "You've joined the webinar.",
        life: 3000,
      });
    },
  });
  isLoading.value = false;
};
</script>

<template>
  <div class="relative bg-primary rounded-md h-[500px]">
    <img
      src="~/assets/img/vector-border.svg"
      alt="vector"
      class="absolute left-0 top-0 bottom-0 h-full object-cover transform scale-x-[-1] rounded-md"
    />
    <div class="flex flex-col lg:flex-row h-full justify-between">
      <div
        class="w-full lg:w-1/2 ml-24 lg:mr-8 pr-12 text-white my-10 px-4 lg:px-0"
      >
        <div class="mb-2">
          <span
            class="rounded-md bg-primary-400 text-surface-50 text-xs px-1.5 py-1"
          >
            Free webinars
          </span>
        </div>

        <h1
          class="lg:text-3xl text-2xl font-bold mb-8 text-wrap whitespace-pre-wrap break-words line-clamp-2"
        >
          {{ webinar?.title }}
        </h1>

        <div class="space-y-6 mb-10">
          <div class="flex gap-4">
            <i class="pi pi-calendar text-2xl"></i>
            <div>
              <p class="text-sm text-primary-100 mb-1.5">Date</p>
              <p class="text-lg font-bold">{{ formattedDate }}</p>
            </div>
          </div>
          <div class="flex gap-4">
            <i class="pi pi-clock text-2xl"></i>
            <div>
              <p class="text-sm text-primary-100 mb-1.5">Time</p>
              <p class="text-lg font-bold">{{ formattedTime }}</p>
            </div>
          </div>
          <div class="flex gap-4">
            <i class="pi pi-flag text-2xl"></i>
            <div>
              <p class="text-sm text-primary-100 mb-1.5">Location</p>
              <p class="text-lg font-bold break-words">
                {{ webinar.meeting_link }}
              </p>
            </div>
          </div>
        </div>

        <Button
          v-if="isJoined"
          label="Join now"
          icon="pi pi-angle-right"
          icon-pos="right"
          severity="tertiary"
          @click="joinWebinar"
        />
        <ProfileCompleteGuard @onCallback="registerWebinar" v-else>
          <Button
            label="Register now"
            icon="pi pi-angle-right"
            icon-pos="right"
            severity="tertiary"
            :loading="isLoading"
            @click="registerWebinar"
          />
        </ProfileCompleteGuard>
      </div>

      <div class="hidden lg:block w-full lg:w-[500px]">
        <img
          v-if="webinar?.media?.length !== 0"
          :src="webinar?.media?.at(0)?.original_url"
          alt="webinar-banner"
          class="h-full w-full object-cover"
        />
        <img
          v-else
          src="~/assets/img/webinar-dummy.jpg"
          alt="webinar-banner"
          class="h-full w-full object-cover"
        />
      </div>
    </div>
  </div>
</template>
